import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { consentConfig } from "../legal/consentConfig";
import "./ParentConsentPopup.css";

export default function ParentConsentPopup({
  visible,
  userPhoneNumber,
  forexistingParent = false,
  onCancel,
  onSuccess,
}) {
  const [currentStep, setCurrentStep] = useState("consent");

  const [optionalChoices, setOptionalChoices] = useState({
    opt_provider_share: false,
    opt_share_health_record: false,
    opt_bio_audio_feature: false,
    opt_bio_video_feature: false,
    opt_train_audio: false,
    opt_train_video: false,
  });

  const [finalAcceptance, setFinalAcceptance] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [referenceId, setReferenceId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  useEffect(() => {
    if (!visible) {
      setCurrentStep("consent");
      setOptionalChoices({
        opt_provider_share: false,
        opt_share_health_record: false,
        opt_bio_audio_feature: false,
        opt_bio_video_feature: false,
        opt_train_audio: false,
        opt_train_video: false,
      });
      setFinalAcceptance(false);
      setIdNumber("");
      setReferenceId(null);
      setOtpCode("");
      setCountdown(0);
      clearInterval(timerRef.current);
    }
  }, [visible]);

  const toggleOptional = (id) => {
    setOptionalChoices((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleConsentProceed = (e) => {
    e.preventDefault();
    if (!finalAcceptance) return;
    setCurrentStep("verification");
  };

  const handleRequestOTP = async () => {
    if (idNumber.trim().length !== 12) {
      return toast.warn("Please enter a valid 12-digit ID number.");
    }

    let url, payload;

    if (forexistingParent) {
      url = `${process.env.REACT_APP_BACKEND_URL}/api/send_existing_parent_aadhar_otp`;
      payload = { aadhaar_number: idNumber };
    } else {
      if (!userPhoneNumber) {
        return toast.error("Phone number missing. Please update the form.");
      }
      url = `${process.env.REACT_APP_BACKEND_URL}/api/send_aadhar_otp`;
      payload = { aadhaar_number: idNumber, phone_number: userPhoneNumber, user_type: "parent" };
    }

    setLoading(true);
    try {
      const res = await axios.post(url, payload);

      if (res.data && res.data.success && res.data.reference_id) {
        setReferenceId(res.data.reference_id);
        setCountdown(60);
        toast.success("Verification code dispatched successfully.");
      } else {
        toast.error(
          res.data?.message ||
            res.data?.error ||
            "Failed to initiate verification.",
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to connect to verification services.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.trim().length !== 6) {
      return toast.warn("Please enter the 6-digit verification code.");
    }

    setLoading(true);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/verify_aadhar_otp`;
    try {
      const res = await axios.post(url, {
        reference_id: referenceId,
        otp: otpCode,
      });

      if (res.data && res.data.success) {
        const selectedScopeArray = Object.keys(optionalChoices).filter(
          (key) => optionalChoices[key],
        );
        onSuccess({
          consentScope: selectedScopeArray,
          required_consent_scope: finalAcceptance,
          transactionReceipt: res.data?.transaction_receipt,
          age: res.data?.age || null,
          aadhaar_number: idNumber,
          consent_form_version: consentConfig.version,
        });
      } else {
        toast.error(
          res.data?.message || res.data?.error || "Incorrect validation code.",
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Error finalizing verification state.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="term-privacy-overlay"
        onClick={!loading ? onCancel : undefined}
      ></div>
      <div className="term-privacy-popup consent-popup-card">
        {/* Header Strip */}
        <div className="consent-header-block">
          <h2 className="consent-brand-title">
            {consentConfig.ui_labels.meta_app}
          </h2>
          <p className="consent-sub-title">
            {currentStep === "consent"
              ? `${consentConfig.ui_labels.title} — ${consentConfig.ui_labels.meta_for})`
              : "Identity Verification — confirm it's really you"}
          </p>
          {currentStep === "consent" && (
            <div className="consent-legend-row">
              <span className="consent-badge-pill required">
                {consentConfig.ui_labels.pill_required}
              </span>
              <span className="consent-badge-pill optional">
                {consentConfig.ui_labels.pill_optional}
              </span>
            </div>
          )}
        </div>

        {/* STEP 1: PARSING LEGAL TERMS */}
        {currentStep === "consent" && (
          <div className="consent-body-scroll">
            {/* Purpose */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.purpose.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.purpose.p1}
              </p>
              <p
                className="consent-lead-para"
                style={{ fontSize: "13px", marginTop: "6px" }}
              >
                {consentConfig.cards.purpose.p2_muted}
              </p>
            </div>

            {/* What we collect */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.collect.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.collect.p1}
              </p>
              <div
                className="consent-callout-panel"
                style={{ marginTop: "8px", marginBottom: "8px" }}
              >
                {consentConfig.cards.collect.bullets.map((bullet, idx) => (
                  <p key={idx} className="consent-bullet-line">
                    • {bullet}
                  </p>
                ))}
              </div>
              <p className="consent-lead-para" style={{ fontSize: "13px" }}>
                {consentConfig.cards.collect.p2_muted}
              </p>
            </div>

            {/* Benefits */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.benefit.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.benefit.p1}
              </p>
              <ul
                className="consent-legal-bullets"
                style={{ marginTop: "8px" }}
              >
                {consentConfig.cards.benefit.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Access Control */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.access.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.access.p1}
              </p>
              <ul
                className="consent-legal-bullets"
                style={{ marginTop: "8px" }}
              >
                {consentConfig.cards.access.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
              <p
                className="consent-bullet-line"
                style={{ fontStyle: "italic", fontSize: "13px" }}
              >
                {consentConfig.cards.access.note}
              </p>
            </div>

            {/* Data Usage */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.datause.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.datause.p1}
              </p>
              <ul
                className="consent-legal-bullets"
                style={{ marginTop: "8px" }}
              >
                {consentConfig.cards.datause.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
              <p
                className="consent-section-description"
                style={{ fontStyle: "italic" }}
              >
                {consentConfig.cards.datause.p2_muted}
              </p>
            </div>

            {/* Risk Factors */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.risk.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.risk.p1}
              </p>
              <ul
                className="consent-legal-bullets"
                style={{ marginTop: "8px" }}
              >
                {consentConfig.cards.risk.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Rights Framework */}
            <div className="consent-section-block">
              <h3 className="consent-section-title">
                {consentConfig.cards.rights.lbl}
              </h3>
              <p className="consent-section-description">
                {consentConfig.cards.rights.p1}
              </p>
              <ul
                className="consent-legal-bullets"
                style={{ marginTop: "8px" }}
              >
                {consentConfig.cards.rights.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
              </ul>
            </div>

            <hr style={{ margin: "18px 0" }} />

            {/* Optional Records choices */}
            <h3 className="consent-choice-list-title">
              {consentConfig.cards.optcard.lbl}
            </h3>
            <p className="consent-lead-para">
              {consentConfig.cards.optcard.p1}
            </p>
            <p className="consent-bullet-line" style={{ fontWeight: "bold" }}>
              {consentConfig.cards.optcard.grp}
            </p>
            {consentConfig.cards.optcard.choices.map((opt) => (
              <label key={opt.id} className="consent-pressable-row">
                <input
                  type="checkbox"
                  className="consent-row-input"
                  checked={!!optionalChoices[opt.id]}
                  onChange={() => toggleOptional(opt.id)}
                />
                <div className="consent-row-details">
                  <strong>{opt.title}</strong>
                  <p>{opt.desc}</p>
                </div>
              </label>
            ))}

            {/* Voice & Video Features */}
            <h3 className="consent-choice-list-title">
              {consentConfig.cards.biocard.lbl}
            </h3>
            <p className="consent-lead-para">
              {consentConfig.cards.biocard.p1}
            </p>
            <ul className="consent-legal-bullets" style={{ marginTop: "8px" }}>
              {consentConfig.cards.biocard.bullets.map((bullet, idx) => (
                <li key={idx}>{bullet}</li>
              ))}
            </ul>
            <p className="consent-bullet-line" style={{ fontWeight: "bold" }}>
              {consentConfig.cards.biocard.grp1}
            </p>
            {consentConfig.cards.biocard.choices_features.map((opt) => (
              <label key={opt.id} className="consent-pressable-row">
                <input
                  type="checkbox"
                  className="consent-row-input"
                  checked={!!optionalChoices[opt.id]}
                  onChange={() => toggleOptional(opt.id)}
                />
                <div className="consent-row-details">
                  <span
                    className="consent-badge-pill optional"
                    style={{ display: "inline-block", marginBottom: "4px" }}
                  >
                    {opt.tag}
                  </span>
                  <strong>{opt.title}</strong>
                  <p>{opt.desc}</p>
                </div>
              </label>
            ))}

            {/* AI Training Optimizations */}
            <p className="consent-bullet-line" style={{ fontWeight: "bold" }}>
              {consentConfig.cards.biocard.grp2}
            </p>
            <p className="consent-section-description">
              {consentConfig.cards.biocard.grp2_desc}
            </p>
            {consentConfig.cards.biocard.choices_improvements.map((opt) => (
              <label key={opt.id} className="consent-pressable-row">
                <input
                  type="checkbox"
                  className="consent-row-input"
                  checked={!!optionalChoices[opt.id]}
                  onChange={() => toggleOptional(opt.id)}
                />
                <div className="consent-row-details">
                  <span
                    className="consent-badge-pill optional"
                    style={{ display: "inline-block", marginBottom: "4px" }}
                  >
                    {opt.tag}
                  </span>
                  <strong>{opt.title}</strong>
                </div>
              </label>
            ))}
            <p
              className="consent-bullet-line"
              style={{
                fontStyle: "italic",
                marginTop: "8px",
                fontSize: "13px",
              }}
            >
              {consentConfig.cards.biocard.note}
            </p>

            <hr style={{ margin: "18px 0" }} />

            {/* Legal Attestation Frame */}
            <div className="consent-attestation-container">
              <h3>{consentConfig.cards.consent.lbl}</h3>
              <p>{consentConfig.cards.consent.p1}</p>
              <ul className="consent-legal-bullets">
                {consentConfig.cards.consent.bullets.map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                ))}
                <li>
                  {consentConfig.cards.consent.legal_links.prefix}
                  <a
                    href="https://smile2steps.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="consent-legal-link"
                  >
                    {consentConfig.cards.consent.legal_links.privacy}
                  </a>
                  {consentConfig.cards.consent.legal_links.mid}
                  <a
                    href="https://smile2steps.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="consent-legal-link"
                  >
                    {consentConfig.cards.consent.legal_links.terms}
                  </a>
                  {consentConfig.cards.consent.legal_links.suffix}
                </li>
              </ul>

              <label
                className={`consent-checkbox-lock-row ${finalAcceptance ? "checked" : ""}`}
              >
                <input
                  type="checkbox"
                  style={{ width: "18px", height: "18px" }}
                  checked={finalAcceptance}
                  onChange={() => setFinalAcceptance(!finalAcceptance)}
                />
                <strong>{consentConfig.cards.consent.checkboxLabel} *</strong>
              </label>
            </div>

            {/* Action Triggers Grid */}
            <div className="consent-action-footer-row">
              {!forexistingParent && <button
                type="button"
                onClick={onCancel}
                style={{
                  padding: "14px 28px",
                  borderRadius: "14px",
                  border: "1.5px solid var(--color-border)",
                  cursor: "pointer",
                  background: "none",
                  fontWeight: "600",
                  color: "var(--color-text-secondary)",
                }}
              >
                {consentConfig.ui_labels.btn_decline}
              </button>}
              <button
                type="button"
                disabled={!finalAcceptance}
                onClick={handleConsentProceed}
                className="verify-submit-btn"
                style={{ width: "220px", padding: "14px" }}
              >
                {consentConfig.ui_labels.btn_agree}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: IDENTITY VERIFICATION CONTAINER */}
        {currentStep === "verification" && (
          <div className="verify-inner-layout">
            <p className="verify-instruction-text">
              {!referenceId
                ? "Enter your 12-digit Aadhaar number to verify your account."
                : "We've sent a 6-digit secure code to your registered mobile number."}
            </p>

            <div className="verify-field-block">
              <label className="verify-field-label">Aadhaar Number</label>
              <input
                type="text"
                className="verify-core-input-id"
                maxLength={12}
                placeholder="Enter 12-digit Aadhaar"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ""))}
                disabled={loading || !!referenceId}
              />
            </div>

            {!referenceId && (
              <button
                type="button"
                className="verify-submit-btn"
                onClick={handleRequestOTP}
                disabled={loading}
              >
                {loading ? "Sending..." : "Get Verification Code"}
              </button>
            )}

            {!!referenceId && (
              <div style={{ width: "100%" }}>
                <div className="verify-field-block">
                  <label className="verify-field-label">Enter OTP</label>
                  <input
                    type="text"
                    className="verify-core-input-otp"
                    maxLength={6}
                    placeholder="X X X X X X"
                    value={otpCode}
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, ""))
                    }
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="verify-resend-link-container">
                  {countdown > 0 ? (
                    <span className="verify-resend-countdown">
                      Resend code in {countdown}s
                    </span>
                  ) : (
                    <span
                      className="verify-resend-clickable"
                      onClick={!loading ? handleRequestOTP : undefined}
                    >
                      Didn't receive code? Resend
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="verify-submit-btn"
                  onClick={handleVerifyOTP}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify & Continue"}
                </button>
              </div>
            )}

            <div className="verify-cancel-btn-wrapper">
              <span
                className="verify-cancel-link-text"
                onClick={!loading ? onCancel : undefined}
              >
                Cancel
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
